/** Push-to-talk: клавиша клавиатуры (KeyboardEvent.code), средняя или боковая кнопка мыши (Mouse3/Mouse4/Mouse5). */
import { mouseButtonOf, mouseCode } from '@/lib/hotkeys'
import { consumePress } from '@/lib/mousePress'

const KEY_LABELS: Record<string, string> = {
  Backquote: '`',
  Space: 'Пробел',
  ControlLeft: 'Ctrl',
  ControlRight: 'Ctrl (правый)',
  ShiftLeft: 'Shift',
  ShiftRight: 'Shift (правый)',
  AltLeft: 'Alt',
  AltRight: 'Alt (правый)',
  MetaLeft: 'Cmd',
  MetaRight: 'Cmd (правый)',
  CapsLock: 'Caps Lock',
  Tab: 'Tab',
  Mouse3: 'Средняя кнопка мыши',
  Mouse4: 'Кнопка мыши 4',
  Mouse5: 'Кнопка мыши 5',
}

/** Кнопки мыши, которые годятся для рации: средняя и боковые. Левой и правой пользуются. */
const PTT_BUTTONS = new Set([1, 3, 4])

export function keyLabel(code: string): string {
  if (!code) return 'не назначена'
  if (KEY_LABELS[code]) return KEY_LABELS[code]
  return code.replace(/^Key/, '').replace(/^Digit/, '').replace(/^Numpad/, 'Num ').replace(/^Arrow/, '')
}

/** Курсор сейчас в поле ввода — глобальный PTT не должен срабатывать на набор текста. */
export function typingInInput(): boolean {
  return document.hasFocus() && editing(document.activeElement)
}

function editing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el || typeof el.closest !== 'function') return false
  return Boolean(el.closest('input, textarea, select, [contenteditable="true"]'))
}

/** Слушает клавишу PTT, пока не вызван возвращённый stop; в полях ввода клавиша не перехватывается. */
export function listenPTT(code: string, onChange: (down: boolean) => void): () => void {
  let down = false
  const set = (v: boolean) => {
    if (down === v) return
    down = v
    onChange(v)
  }
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.code !== code || editing(e.target)) return
    e.preventDefault()
    set(true)
  }
  const onKeyUp = (e: KeyboardEvent) => {
    if (e.code !== code) return
    set(false)
  }
  const mouseButton = mouseButtonOf(code)
  // нажатие гасим целиком: боковая кнопка иначе пролистала бы историю и увела со страницы;
  // фаза захвата — чтобы отпускание не потерялось, если кто-то ниже остановит событие
  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== mouseButton) return
    consumePress(e)
    set(true)
  }
  const onMouseUp = (e: MouseEvent) => {
    if (e.button !== mouseButton) return
    e.preventDefault()
    set(false)
  }
  const onBlur = () => set(false)
  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('keyup', onKeyUp)
  window.addEventListener('blur', onBlur)
  if (PTT_BUTTONS.has(mouseButton)) {
    window.addEventListener('mousedown', onMouseDown, true)
    window.addEventListener('mouseup', onMouseUp, true)
  }
  return () => {
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('keyup', onKeyUp)
    window.removeEventListener('blur', onBlur)
    window.removeEventListener('mousedown', onMouseDown, true)
    window.removeEventListener('mouseup', onMouseUp, true)
    set(false)
  }
}

/** Кнопка мыши рации или -1; левая и правая ей не бывают. */
export function pttMouseButton(code: string): number {
  const button = mouseButtonOf(code)
  return PTT_BUTTONS.has(button) ? button : -1
}

/** Ждёт следующую клавишу, среднюю или боковую кнопку мыши для назначения; Escape — отмена (null). */
export function captureKey(): Promise<string | null> {
  return new Promise((resolve) => {
    const done = (code: string | null) => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('mousedown', onMouse, true)
      resolve(code)
    }
    const onKey = (e: KeyboardEvent) => {
      e.preventDefault()
      e.stopPropagation()
      done(e.code === 'Escape' ? null : e.code)
    }
    // левая и правая проходят мимо: ими нажимают «Отмена» и всё остальное
    const onMouse = (e: MouseEvent) => {
      if (!PTT_BUTTONS.has(e.button)) return
      consumePress(e)
      e.stopPropagation()
      done(mouseCode(e.button))
    }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('mousedown', onMouse, true)
  })
}
